/**
 * The client brief: a preview of exactly what the PDF prints (shareable content only: no scores, gaps or near
 * misses), shown as plain text because the model wrote it. The seller who started the run confirms it may go to the
 * client; the brief is stored as confirmed and the PDF prints that stored brief only. When the brief as it reads now
 * differs from the confirmed one (an asset changed since), the dialog says so, shows both, and the seller confirms
 * again to replace it. Confirming names the previewed brief by its hash; if the brief changed meanwhile the server
 * refuses ("The brief changed; review it again."), and the dialog shows the brief as it reads now to review.
 */

import { CheckCircle2, FileDown } from 'lucide-react';

import { useBriefPdf, useConfirmClientBrief } from '@/api/queries';
import type { AdvisorRun, ClientBrief } from '@/api/types';
import { ActionButton, buttonState } from '@/ui/ActionButton';
import { formatDateTime } from '@/ui/format';
import { Modal } from '@/ui/Modal';
import { FormError } from '@/ui/StateCard';

import { briefChangedText, personName } from './advisorUi';

function Preview({ brief, label }: { brief: ClientBrief; label: string }) {
  return (
    <div className="advisor-brief-preview stack-md" aria-label={label}>
      <h3>Proposed GFT assets: {brief.client}</h3>
      <section className="stack-sm">
        <h4>What we heard</h4>
        {brief.summary && <p>{brief.summary}</p>}
        <ul className="advisor-list">{brief.needs.map((n, i) => <li key={i}>{n}</li>)}</ul>
      </section>
      <section className="stack-sm">
        <h4>What GFT brings</h4>
        {brief.assets.map((a) => (
          <div key={a.name} className="stack-sm">
            <p><b>{a.name}</b> <span className="hint">{a.kind} · Maturity: {a.maturity}</span></p>
            {a.tagline && <p>{a.tagline}</p>}
            <ul className="advisor-list">{a.how.map((h, i) => <li key={i}><b>{h.need}</b>: {h.how}</li>)}</ul>
          </div>
        ))}
      </section>
      {brief.combinations.length > 0 && (
        <section className="stack-sm">
          <h4>Working together</h4>
          <ul className="advisor-list">{brief.combinations.map((c, i) => <li key={i}>{c}</li>)}</ul>
        </section>
      )}
      <p className="hint">{brief.prepared_line}</p>
    </div>
  );
}

function Status({ run }: { run: AdvisorRun }) {
  if (run.confirmed_client_brief) {
    return (
      <p className="hint" role="status">
        Confirmed by {personName(run.client_brief_confirmed_by)} on {formatDateTime(run.client_brief_confirmed_at)}.
        The PDF prints the brief as confirmed.
      </p>
    );
  }
  if (!run.can.confirm_client_brief) {
    return <p className="hint" role="status">The person who started this run has not confirmed this brief yet.</p>;
  }
  return <p className="hint">Read it as the client will. Confirm only if everything in it may leave GFT.</p>;
}

function Briefs({ run }: { run: AdvisorRun }) {
  const confirmed = run.confirmed_client_brief;
  if (!confirmed) return run.client_brief ? <Preview brief={run.client_brief} label="Client brief preview" /> : null;
  if (!run.client_brief_changed) return <Preview brief={confirmed} label="Confirmed client brief" />;
  return (
    <>
      <div className="alert alert-warning" role="note">{briefChangedText(run)}</div>
      <h3 className="advisor-title">As confirmed (what the PDF prints)</h3>
      <Preview brief={confirmed} label="Confirmed client brief" />
      {run.client_brief && (
        <>
          <h3 className="advisor-title">As it reads now</h3>
          <Preview brief={run.client_brief} label="Client brief preview" />
        </>
      )}
    </>
  );
}

export function ClientBriefDialog({ run, onClose }: { run: AdvisorRun; onClose: () => void }) {
  const confirm = useConfirmClientBrief(run.id);
  const pdf = useBriefPdf(run.id);
  const confirmed = Boolean(run.confirmed_client_brief);
  const canConfirm = run.can.confirm_client_brief && run.client_brief !== null && (!confirmed || run.client_brief_changed);
  return (
    <Modal
      title="Client brief"
      subtitle="Shareable content only: no scores, gaps or near misses."
      size="wide"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
          {canConfirm && (
            <ActionButton
              type="button"
              state={buttonState(confirm)}
              icon={<CheckCircle2 aria-hidden="true" />}
              label={confirmed ? 'Confirm again' : 'Confirm for the client'}
              pendingLabel="Confirming"
              doneLabel="Confirmed"
              onClick={() => confirm.mutate(run.client_brief_hash ?? '')}
            />
          )}
          {run.can.client_brief && (
            <ActionButton
              type="button"
              state={buttonState(pdf)}
              icon={<FileDown aria-hidden="true" />}
              label="Download PDF"
              pendingLabel="Preparing"
              doneLabel="Opened"
              onClick={() => pdf.mutate('client')}
            />
          )}
        </>
      }
    >
      <div className="stack-md">
        <FormError error={confirm.error} />
        <Status run={run} />
        <Briefs run={run} />
      </div>
    </Modal>
  );
}
